import { useState, useRef, useEffect } from 'react';
import { Scale, Briefcase, Building2, Gavel, Users, Shield, Home, FileText, HeartHandshake, Lightbulb, Calculator, AlertTriangle, CheckCircle2, BookOpen, Landmark, TrendingUp, Handshake, Globe, ChevronLeft, ChevronRight, ChevronDown, Award, Briefcase as BriefcaseIcon, Search, X, Filter, Zap } from 'lucide-react';


export default function PracticeAreas() {
  const [isMobile, setIsMobile] = useState(false);
  const scrollContainerRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [expandedCard, setExpandedCard] = useState(null); // Using unique ID instead of index
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchInputRef = useRef(null);


  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);


  const practices = [
    // ED Practice - FEATURED (RED CARD)
    {
      id: 'ed-enforcement', // UNIQUE ID ADDED
      title: "ED & Economic Offences – Key Enforcement Actions",
      icon: AlertTriangle,
      category: "Enforcement",
      image: "https://images.unsplash.com/photo-1568667256549-094345857637?w=800&q=80",
      keyActs: "PMLA, 2002; FEMA, 1999; FERA (Legacy); CGST Act, 2017; Income Tax Act, 1961",
      description: "Expert defense in ED investigations, raids, and economic offence prosecutions.",
      casesHandled: 350,
      successRate: 98,
      featured: true,
      services: [
        "ED Raids & Searches under economic offence laws",
        "ED Property Attachment Proceedings (Provisional & Final)",
        "ED Summons, Arrests & Prosecution",
        "Defence in ED Investigations & Adjudication",
        "PMLA - Money laundering investigations & tainted property attachment",
        "PMLA - Proceedings before Adjudicating Authority & Special Courts",
        "FEMA - Foreign exchange violations & cross-border transactions",
        "FEMA - Compounding & appellate proceedings",
        "FERA - Legacy foreign exchange offences & pending appeals",
        "GST Raids & Search Operations",
        "GST Property / Bank Account Attachment",
        "Fake Invoice & Input Tax Credit (ITC) Fraud Cases",
        "GST Arrests, Bail & Criminal Prosecution",
        "Income Tax Raids & Search Proceedings",
        "Attachment of Assets & Bank Accounts",
        "Undisclosed Income & Benami Transactions",
        "Income Tax Prosecution & Penalty Defence",
        "Pre-raid advisory & compliance strategy",
        "Post-raid legal representation",
        "Quashing of illegal attachments",
        "Bail & anticipatory bail in economic offences",
        "Representation before Tribunals, Special Courts & High Courts",
      ],
    },
    // NEW: Economic Offences & White-Collar Crimes (SEPARATE CARD - RED)
    {
      id: 'economic-white-collar', // UNIQUE ID
      title: "Economic Offences & White-Collar Crimes",
      icon: AlertTriangle,
      category: "Enforcement",
      image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80",
      keyActs: "IPC/BNS, 2023; Companies Act, 2013; PC Act, 1988",
      description: "Sophisticated defense in complex economic and white-collar crime cases including fraud, corruption, and corporate criminal liability.",
      casesHandled: 310,
      successRate: 89,
      featured: true, // ALSO FEATURED/RED
      services: [
        "Fraud & financial crime defence",
        "Corporate criminal liability",
        "SFIO investigations & proceedings",
        "Serious fraud & mismanagement cases",
        "Bank fraud & financial irregularities",
        "Cheating & criminal breach of trust (Sec 420, 406 IPC/BNS)",
        "Forgery & falsification of accounts",
        "Ponzi schemes & multilevel marketing frauds",
        "Insider trading & securities fraud",
        "Cross-border economic offences",
        "Multi-agency investigation coordination (ED, CBI, IT, GST)",
        "Corporate governance violations",
        "Director disqualification matters",
        "Prevention of Corruption Act proceedings",
        "Public servant corruption cases",
      ],
    },
    {
      id: 'corporate-commercial',
      title: "Corporate & Commercial Laws",
      icon: Building2,
      category: "Business",
      image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80",
      keyActs: "Companies Act, 2013; LLP Act, 2008; FEMA, 1999; SEBI Regulations",
      description: "Comprehensive corporate legal solutions for businesses of all sizes, from startups to established enterprises.",
      casesHandled: 450,
      successRate: 96,
      services: [
        "Company incorporation (Private, Public, OPC, Section 8)",
        "LLP formation and compliance",
        "Corporate governance & secretarial compliance",
        "Drafting & vetting of commercial contracts",
        "Shareholders' agreements & JV agreements",
        "Mergers & Acquisitions (M&A)",
        "Private equity & venture capital transactions",
        "Due diligence (legal & compliance)",
        "FEMA & RBI compliance (FDI, ODI, ECB)",
        "SEBI compliance for listed companies",
        "Corporate restructuring & amalgamation",
        "Startup advisory & compliance",
      ],
    },
    {
      id: 'civil-litigation',
      title: "Civil Litigation & Dispute Resolution",
      icon: Scale,
      category: "Litigation",
      image: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&q=80",
      keyActs: "Code of Civil Procedure, 1908; Indian Evidence Act, 1872",
      description: "Expert representation in civil courts for property disputes, contract breaches, and commercial conflicts.",
      casesHandled: 680,
      successRate: 94,
      services: [
        "Civil suits & trials",
        "Injunctions & recovery suits",
        "Declaratory & specific performance suits",
        "Property & land disputes",
        "Commercial disputes",
        "Execution proceedings",
        "Appeals before District Courts & High Courts",
        "Summary suits",
        "Writ petitions",
      ],
    },
    {
      id: 'criminal-law',
      title: "Criminal Law",
      icon: Shield,
      category: "Litigation",
      image: "https://images.unsplash.com/photo-1505664194779-8beaceb93744?w=800&q=80",
      keyActs: "Bharatiya Nyaya Sanhita, 2023; Bharatiya Nagarik Suraksha Sanhita, 2023",
      description: "Aggressive criminal defense and strategic legal representation for all criminal matters.",
      casesHandled: 520,
      successRate: 92,
      services: [
        "Criminal trials & defence",
        "FIR quashing",
        "Bail & anticipatory bail",
        "White-collar crime defence",
        "Cheque bounce cases",
        "Cybercrime defence",
        "Economic offences",
        "Trial & appellate advocacy",
      ],
    },
    {
      id: 'arbitration-adr',
      title: "Arbitration, Mediation & ADR",
      icon: HeartHandshake,
      category: "Dispute Resolution",
      image: "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&q=80",
      keyActs: "Arbitration and Conciliation Act, 1996",
      description: "Alternative dispute resolution for efficient and cost-effective conflict management.",
      casesHandled: 320,
      successRate: 98,
      services: [
        "Domestic & international arbitration",
        "Institutional arbitration",
        "Drafting arbitration clauses",
        "Appointment of arbitrators",
        "Enforcement & challenge of arbitral awards",
        "Mediation & conciliation",
        "Commercial dispute resolution",
      ],
    },
    {
      id: 'banking-finance',
      title: "Banking & Finance Laws",
      icon: Briefcase,
      category: "Financial",
      image: "https://images.unsplash.com/photo-1501167786227-4cba60f6d58f?w=800&q=80",
      keyActs: "SARFAESI Act, 2002; Recovery of Debts Act, 1993",
      description: "Specialized banking and finance legal services for lenders and borrowers.",
      casesHandled: 410,
      successRate: 95,
      services: [
        "Loan documentation",
        "Debt recovery proceedings",
        "DRT & DRAT matters",
        "SARFAESI actions",
        "Insolvency proceedings for lenders",
        "Security enforcement",
        "Financial restructuring",
      ],
    },
    {
      id: 'insolvency-bankruptcy',
      title: "Insolvency & Bankruptcy",
      icon: FileText,
      category: "Financial",
      image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80",
      keyActs: "Insolvency and Bankruptcy Code, 2016",
      description: "Strategic guidance through complex insolvency and bankruptcy proceedings.",
      casesHandled: 280,
      successRate: 93,
      services: [
        "Corporate Insolvency Resolution Process (CIRP)",
        "Liquidation proceedings",
        "Representation before NCLT & NCLAT",
        "Insolvency advisory for creditors & debtors",
        "Personal insolvency & bankruptcy",
        "Resolution plan drafting & vetting",
      ],
    },
    {
      id: 'real-estate',
      title: "Real Estate & Property Laws",
      icon: Home,
      category: "Property",
      image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80",
      keyActs: "Transfer of Property Act, 1882; RERA Act, 2016",
      description: "Complete real estate legal solutions from property transactions to dispute resolution.",
      casesHandled: 590,
      successRate: 97,
      services: [
        "Property due diligence & title search",
        "Sale, lease & license documentation",
        "Builder-buyer disputes",
        "RERA registration & compliance",
        "Land acquisition advisory",
        "Redevelopment projects",
        "Property litigation",
      ],
    },
    {
      id: 'labour-employment',
      title: "Labour & Employment Laws",
      icon: Users,
      category: "Employment",
      image: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&q=80",
      keyActs: "Industrial Disputes Act, 1947; Shops & Establishments Acts; Labour Codes",
      description: "Comprehensive employment law services for employers and employees.",
      casesHandled: 440,
      successRate: 91,
      services: [
        "Employment contracts & HR policies",
        "Labour law compliance",
        "Disciplinary proceedings",
        "Termination & retrenchment advisory",
        "Industrial dispute resolution",
        "POSH compliance",
        "Employee benefits & welfare advisory",
      ],
    },
    {
      id: 'family-matrimonial',
      title: "Family & Matrimonial Laws",
      icon: HeartHandshake,
      category: "Personal",
      image: "https://images.unsplash.com/photo-1511895426328-dc8714191300?w=800&q=80",
      keyActs: "Hindu Marriage Act, 1955; Special Marriage Act, 1954",
      description: "Sensitive and compassionate representation in family law matters.",
      casesHandled: 620,
      successRate: 89,
      services: [
        "Divorce & separation",
        "Child custody & visitation",
        "Maintenance & alimony",
        "Domestic violence cases",
        "Adoption & guardianship",
        "Succession & inheritance disputes",
      ],
    },
    {
      id: 'ipr',
      title: "Intellectual Property Rights (IPR)",
      icon: Lightbulb,
      category: "Business",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80",
      keyActs: "Trade Marks Act, 1999; Copyright Act, 1957; Patents Act, 1970",
      description: "Protect and enforce your intellectual property rights with expert legal guidance.",
      casesHandled: 350,
      successRate: 96,
      services: [
        "Trademark registration & opposition",
        "Copyright registration",
        "Patent filing & prosecution",
        "IP licensing & assignment",
        "IP infringement & enforcement",
        "Brand protection strategy",
        "IP due diligence",
      ],
    },
    {
      id: 'taxation',
      title: "Taxation Laws",
      icon: Calculator,
      category: "Tax",
      image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80",
      keyActs: "Income Tax Act, 1961; GST Acts, 2017",
      description: "Strategic tax planning and litigation services for individuals and businesses.",
      casesHandled: 530,
      successRate: 94,
      services: [
        "Direct & indirect tax advisory",
        "GST registration & compliance",
        "Tax litigation & appeals",
        "Income tax assessments",
        "Transfer pricing advisory",
        "Tax structuring & planning",
      ],
    },
    {
      id: 'constitutional-admin',
      title: "Constitutional & Administrative Law",
      icon: Landmark,
      category: "Public Law",
      image: "https://images.unsplash.com/photo-1589994965851-a8f479c573a9?w=800&q=80",
      keyActs: "Constitution of India",
      description: "High-level constitutional and administrative law representation.",
      casesHandled: 180,
      successRate: 95,
      services: [
        "Writ petitions (Article 226 & 32)",
        "Public Interest Litigations (PIL)",
        "Government tenders & contracts",
        "Regulatory compliance",
        "Judicial review matters",
      ],
    },
    {
      id: 'consumer-protection',
      title: "Consumer Protection Laws",
      icon: Handshake,
      category: "Consumer",
      image: "https://images.unsplash.com/photo-1556742111-a301076d9d18?w=800&q=80",
      keyActs: "Consumer Protection Act, 2019",
      description: "Strong consumer rights protection and product liability representation.",
      casesHandled: 390,
      successRate: 93,
      services: [
        "Consumer complaints & representation",
        "Product liability cases",
        "E-commerce disputes",
        "Appeals before State & National Commissions",
      ],
    },
    {
      id: 'it-cyber',
      title: "Information Technology & Cyber Laws",
      icon: Globe,
      category: "Technology",
      image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800&q=80",
      keyActs: "Information Technology Act, 2000",
      description: "Cutting-edge legal solutions for digital and cybersecurity challenges.",
      casesHandled: 270,
      successRate: 92,
      services: [
        "Data protection & privacy advisory",
        "Cybercrime litigation",
        "Technology contracts",
        "Digital fraud cases",
        "IT compliance audits",
      ],
    },
    {
      id: 'environmental',
      title: "Environmental Laws",
      icon: BookOpen,
      category: "Environmental",
      image: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=800&q=80",
      keyActs: "Environment Protection Act, 1986; NGT Act, 2010",
      description: "Environmental compliance and litigation for sustainable business practices.",
      casesHandled: 150,
      successRate: 91,
      services: [
        "Environmental clearances",
        "Pollution control compliance",
        "NGT litigation",
        "Sustainability & ESG advisory",
      ],
    },
    {
      id: 'regulatory-compliance',
      title: "Regulatory & Compliance Services",
      icon: Shield,
      category: "Compliance",
      image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80",
      keyActs: "Various Regulatory Frameworks",
      description: "Comprehensive regulatory compliance management and advisory services.",
      casesHandled: 480,
      successRate: 97,
      services: [
        "Licenses & approvals",
        "Legal audits",
        "Risk & compliance management",
        "Policy drafting",
        "Corporate compliance management",
      ],
    },
    {
      id: 'notarial-docs',
      title: "Notarial & Documentation Services",
      icon: FileText,
      category: "Documentation",
      image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80",
      keyActs: "Notaries Act, 1952",
      description: "Professional documentation and notarial services for all legal needs.",
      casesHandled: 850,
      successRate: 99,
      services: [
        "Drafting legal notices",
        "Affidavits & declarations",
        "MOUs & agreements",
        "Power of Attorney",
        "Notarisation & attestation",
      ],
    },
    {
      id: 'cbi-matters',
      title: "Central Bureau of Investigation (CBI) Matters",
      icon: Shield,
      category: "Enforcement",
      image: "https://images.unsplash.com/photo-1589994965851-a8f479c573a9?w=800&q=80",
      keyActs: "Bharatiya Nyaya Sanhita, 2023; PC Act, 1988",
      description: "Strategic defense in CBI investigations and corruption cases.",
      casesHandled: 180,
      successRate: 88,
      services: [
        "Representation in CBI investigations",
        "Defence in corruption & economic offence cases",
        "Representation during inquiry & charge-sheet stage",
        "Regular & anticipatory bail matters",
        "Quashing of FIRs & proceedings",
        "Trial & appellate advocacy in CBI courts",
        "Prevention of Corruption Act matters",
        "White-collar crime defence",
        "Public servant & corporate defence",
      ],
    },
    {
      id: 'gst-matters',
      title: "Goods and Services Tax (GST) Matters",
      icon: Calculator,
      category: "Tax",
      image: "https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=800&q=80",
      keyActs: "CGST Act, 2017; SGST Acts; IGST Act",
      description: "Complete GST advisory, compliance, and litigation services.",
      casesHandled: 560,
      successRate: 95,
      services: [
        "GST registration, cancellation & amendments",
        "GST advisory & compliance management",
        "Show cause notices & adjudication proceedings",
        "GST audits, inspections & search proceedings",
        "GST anti-evasion & DGGI matters",
        "Input tax credit (ITC) disputes",
        "GST refund & export-related matters",
        "Representation before Appellate Authorities & Tribunals",
        "GST prosecution & compounding",
        "Writ petitions relating to GST disputes",
      ],
    },
    {
      id: 'income-tax',
      title: "Income Tax (IT) & Direct Tax Matters",
      icon: TrendingUp,
      category: "Tax",
      image: "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&q=80",
      keyActs: "Income Tax Act, 1961; Black Money Act, 2015",
      description: "Expert income tax defense and assessment representation.",
      casesHandled: 490,
      successRate: 93,
      services: [
        "Income tax assessments & reassessments",
        "Search & seizure (Section 132) matters",
        "Survey proceedings (Section 133A)",
        "Income tax raids & post-raid advisory",
        "Show cause notices & penalty proceedings",
        "Appeals before CIT(A), ITAT, High Courts & Supreme Court",
        "Prosecution & compounding of offences",
        "Benami Transactions advisory",
        "Black Money Act proceedings",
        "Tax evasion & undisclosed income defence",
      ],
    },
  ];


  // Get unique categories
  const categories = ['All', ...new Set(practices.map(p => p.category))];


  // Filter practices based on search and category
  const filteredPractices = practices.filter(practice => {
    const matchesSearch = 
      practice.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      practice.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      practice.keyActs.toLowerCase().includes(searchQuery.toLowerCase()) ||
      practice.services.some(service => service.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'All' || practice.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });


  // Get search suggestions
  const searchSuggestions = searchQuery.length > 1
    ? practices
        .filter(p => 
          p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.services.some(s => s.toLowerCase().includes(searchQuery.toLowerCase()))
        )
        .slice(0, 5)
    : [];


  const scrollToIndex = (index) => {
    if (!scrollContainerRef.current) return;
    
    const container = scrollContainerRef.current;
    const cards = container.children;
    if (cards[index]) {
      const card = cards[index];
      const containerWidth = container.clientWidth;
      const cardLeft = card.offsetLeft;
      const cardWidth = card.offsetWidth;
      
      const scrollPosition = cardLeft - (containerWidth / 2) + (cardWidth / 2);
      
      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }
    
    setCurrentIndex(index);
  };


  const updateCurrentIndex = () => {
    if (!scrollContainerRef.current || !isMobile) return;
    
    const container = scrollContainerRef.current;
    const containerWidth = container.clientWidth;
    const scrollLeft = container.scrollLeft;
    const centerPosition = scrollLeft + (containerWidth / 2);
    
    let closestIndex = 0;
    let closestDistance = Infinity;
    
    Array.from(container.children).forEach((card, index) => {
      const cardCenter = card.offsetLeft + (card.offsetWidth / 2);
      const distance = Math.abs(centerPosition - cardCenter);
      
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    
    setCurrentIndex(closestIndex);
  };


  const scrollNext = () => {
    const nextIndex = (currentIndex + 1) % filteredPractices.length;
    scrollToIndex(nextIndex);
  };


  const scrollPrev = () => {
    const prevIndex = currentIndex === 0 ? filteredPractices.length - 1 : currentIndex - 1;
    scrollToIndex(prevIndex);
  };


  // FIXED: Toggle using unique ID instead of index
  const toggleExpanded = (practiceId) => {
    setExpandedCard(prevId => prevId === practiceId ? null : practiceId);
  };


  const handleSearchSelect = (title) => {
    setSearchQuery(title);
    setShowSuggestions(false);
  };


  const clearSearch = () => {
    setSearchQuery('');
    setShowSuggestions(false);
  };


  useEffect(() => {
    if (isMobile && scrollContainerRef.current) {
      setTimeout(() => scrollToIndex(0), 100);
    }
  }, [isMobile]);


  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchInputRef.current && !searchInputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  const PracticeCard = ({ practice, index }) => {
    const Icon = practice.icon;
    const isExpanded = expandedCard === practice.id; // FIXED: Using unique ID
    
    return (
      <div
        className={`group bg-white rounded-xl shadow-lg border-2 overflow-hidden transition-all duration-300 flex flex-col relative ${
          practice.featured 
            ? 'border-red-500 ring-4 ring-red-500/20' 
            : 'border-slate-200'
        } ${
          !isMobile ? 'hover:shadow-2xl hover:-translate-y-1' : ''
        } ${isMobile ? 'flex-shrink-0 w-[85vw] max-w-[360px]' : ''}`}
      >
        {/* PRIORITY BADGE */}
        {practice.featured && (
          <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5 bg-gradient-to-r from-red-600 to-red-700 text-white px-3 py-1.5 rounded-full shadow-lg animate-pulse">
            <Zap className="w-3.5 h-3.5 fill-white" />
            <span className="text-xs font-bold tracking-wider uppercase">Priority</span>
          </div>
        )}


        {/* IMAGE HEADER */}
        <div className="relative h-44 overflow-hidden flex-shrink-0">
          <img 
            src={practice.image} 
            alt={practice.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent"></div>
          <div className="absolute bottom-4 left-4 right-4">
            <div className="flex items-center gap-2.5 mb-1">
              <div className={`w-11 h-11 rounded-lg flex items-center justify-center shadow-lg flex-shrink-0 ${
                practice.featured ? 'bg-red-600' : 'bg-yellow-500'
              }`}>
                <Icon className={`w-5 h-5 ${practice.featured ? 'text-white' : 'text-slate-900'}`} />
              </div>
              {/* BIGGER TITLE */}
              <h3 className="text-lg sm:text-xl md:text-xl lg:text-[1.35rem] font-bold text-white leading-tight flex-1">
                {practice.title}
              </h3>
            </div>
          </div>
        </div>


        {/* DESCRIPTION */}
        <div className="p-5 flex flex-col flex-1">
          <p className="text-slate-600 text-sm leading-relaxed mb-4 line-clamp-2">
            {practice.description}
          </p>


          {/* STATS */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <div className="flex items-center gap-2 mb-1">
                <BriefcaseIcon className="w-4 h-4 text-slate-600" />
                <span className="text-xs text-slate-600 font-medium">Cases</span>
              </div>
              <p className="text-xl font-bold text-slate-900">{practice.casesHandled}+</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <div className="flex items-center gap-2 mb-1">
                <Award className="w-4 h-4 text-yellow-600" />
                <span className="text-xs text-slate-600 font-medium">Success</span>
              </div>
              <p className="text-xl font-bold text-yellow-600">{practice.successRate}%</p>
            </div>
          </div>


          {/* KEY ACTS */}
          <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-xs text-slate-600 leading-relaxed line-clamp-2">
              <span className="font-semibold text-slate-900">Key Acts:</span> {practice.keyActs}
            </p>
          </div>


          {/* EXPANDABLE SERVICES */}
          <button
            onClick={() => toggleExpanded(practice.id)} // FIXED: Using unique ID
            className={`w-full flex items-center justify-between p-3 text-white rounded-lg transition-colors duration-300 mb-3 ${
              practice.featured 
                ? 'bg-red-600 hover:bg-red-700' 
                : 'bg-slate-900 hover:bg-slate-800'
            }`}
          >
            <span className="font-semibold text-sm">View Services</span>
            <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
          </button>


          {/* SERVICES DRAWER */}
          <div 
            className={`overflow-hidden transition-all duration-300 ${
              isExpanded ? 'max-h-72 opacity-100 mb-4' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 max-h-64 overflow-y-auto">
              <ul className="space-y-2">
                {practice.services.map((service, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-700">
                    <CheckCircle2 className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                      practice.featured ? 'text-red-600' : 'text-yellow-500'
                    }`} />
                    <span className="text-sm leading-relaxed">{service}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>


          {/* LEARN MORE BUTTON */}
          <a
            href="/contact"
            className={`block w-full py-3 font-bold rounded-lg transition-all duration-300 text-sm text-center shadow-md hover:shadow-lg mt-auto ${
              practice.featured 
                ? 'bg-red-600 hover:bg-red-700 text-white' 
                : 'bg-yellow-500 hover:bg-yellow-600 text-slate-900'
            }`}
          >
            Learn More & Consult
          </a>
        </div>
      </div>
    );
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* HERO SECTION */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-12 sm:py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/20 border border-yellow-500/30 rounded-full mb-4 sm:mb-6">
            <Gavel className="w-4 h-4 text-yellow-400" />
            <span className="text-yellow-400 text-xs sm:text-sm font-semibold tracking-widest uppercase">
              Our Expertise
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4 leading-tight">
            Practice <span className="text-yellow-400">Areas</span>
          </h1>
          <p className="text-sm sm:text-base md:text-lg text-slate-300 max-w-3xl mx-auto leading-relaxed mb-6 sm:mb-8">
            Comprehensive legal solutions across 22 specialized practice areas, delivering excellence and results for every client.
          </p>


          {/* ADVANCED SEARCH BAR */}
          <div className="max-w-3xl mx-auto" ref={searchInputRef}>
            <div className="relative">
              <div className="relative flex items-center">
                <Search className="absolute left-4 w-5 h-5 text-slate-400 pointer-events-none z-10" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  placeholder="Search practice areas, services, or legal acts..."
                  className="w-full pl-12 pr-12 py-4 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-300"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors duration-300"
                  >
                    <X className="w-5 h-5 text-slate-400" />
                  </button>
                )}
              </div>


              {/* AUTOCOMPLETE SUGGESTIONS */}
              {showSuggestions && searchSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50 max-h-64 overflow-y-auto">
                  {searchSuggestions.map((suggestion, idx) => (
                    <button
                      key={suggestion.id}
                      onClick={() => handleSearchSelect(suggestion.title)}
                      className="w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors duration-200 border-b border-slate-100 last:border-b-0 flex items-center gap-3"
                    >
                      <suggestion.icon className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-slate-900">{suggestion.title}</p>
                        <p className="text-xs text-slate-600 line-clamp-1">{suggestion.description}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>


            {/* CATEGORY FILTERS */}
            <div className="flex items-center gap-2 mt-4 overflow-x-auto scrollbar-hide pb-2">
              <Filter className="w-4 h-4 text-yellow-400 flex-shrink-0" />
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-300 ${
                    selectedCategory === category
                      ? 'bg-yellow-500 text-slate-900 shadow-lg'
                      : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>


            {/* RESULTS COUNT */}
            {searchQuery || selectedCategory !== 'All' ? (
              <p className="text-sm text-slate-300 mt-3">
                Found <span className="font-bold text-yellow-400">{filteredPractices.length}</span> practice area{filteredPractices.length !== 1 ? 's' : ''}
              </p>
            ) : null}
          </div>
        </div>
      </section>


      {/* PRACTICE AREAS */}
      <section className="max-w-7xl mx-auto py-12 sm:py-16">
        {filteredPractices.length === 0 ? (
          <div className="text-center py-16 px-4">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">No Results Found</h3>
            <p className="text-slate-600 mb-4">Try adjusting your search or filters</p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('All');
              }}
              className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-slate-900 font-semibold rounded-lg transition-colors duration-300"
            >
              Clear Filters
            </button>
          </div>
        ) : isMobile ? (
          <div className="relative">
            <button
              onClick={scrollPrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-slate-900/90 hover:bg-yellow-500 text-white rounded-full flex items-center justify-center shadow-xl transition-all duration-300 hover:scale-110"
              aria-label="Previous card"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={scrollNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-slate-900/90 hover:bg-yellow-500 text-white rounded-full flex items-center justify-center shadow-xl transition-all duration-300 hover:scale-110"
              aria-label="Next card"
            >
              <ChevronRight className="w-5 h-5" />
            </button>


            <div
              ref={scrollContainerRef}
              onScroll={updateCurrentIndex}
              className="flex gap-4 overflow-x-auto scrollbar-hide px-4 snap-x snap-mandatory"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {filteredPractices.map((practice, index) => (
                <div key={practice.id} className="snap-center">
                  <PracticeCard practice={practice} index={index} />
                </div>
              ))}
            </div>


            <div className="flex justify-center gap-2 mt-6 px-4 overflow-x-auto scrollbar-hide">
              {filteredPractices.map((_, index) => (
                <button
                  key={index}
                  onClick={() => scrollToIndex(index)}
                  className={`transition-all duration-300 rounded-full flex-shrink-0 ${
                    currentIndex === index 
                      ? 'w-8 h-2 bg-yellow-500' 
                      : 'w-2 h-2 bg-slate-300 hover:bg-slate-400'
                  }`}
                  aria-label={`Go to card ${index + 1}`}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPractices.map((practice, index) => (
                <PracticeCard key={practice.id} practice={practice} index={index} />
              ))}
            </div>
          </div>
        )}
      </section>


      {/* CTA SECTION */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
            Need Legal Assistance?
          </h2>
          <p className="text-slate-300 text-sm sm:text-base md:text-lg mb-6 leading-relaxed">
            Our experienced team is ready to provide expert guidance tailored to your specific legal needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="px-6 sm:px-8 py-3 bg-yellow-500 hover:bg-yellow-600 text-slate-900 font-bold rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-yellow-500/50 text-sm sm:text-base"
            >
              Schedule Consultation
            </a>
            <a
              href="/contact"
              className="px-6 sm:px-8 py-3 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white font-semibold rounded-lg border border-white/20 transition-all duration-300 text-sm sm:text-base"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>


      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}
